/**
 * Master switchboard for DOM interaction logging (src/services/logger/domTracker.ts).
 *
 * Flip any flag below to `false` to stop logging that category — nothing
 * else needs to change, every handler in domTracker.ts checks its own flag
 * before doing anything. You can also just comment out the corresponding
 * `init...()` call inside `initDomTracking()` in domTracker.ts if you'd
 * rather remove a listener entirely instead of toggling it off.
 */
export const DOM_TRACKING_CONFIG = {
  // Clicks on <button>, <a>, [role="button"], and anything tagged
  // data-log-id. Logs tag name / log-id / visible text / aria-label only —
  // never any value. This is the original, always-safe tracker.
  clicks: true,

  // `change` events on <input>/<textarea> (text, number, date, search,
  // etc.) and MUI TextField/Autocomplete (they render a real <input> under
  // the hood, so the same native listener catches them). Logs field
  // name/id/type/label, how many characters were typed (`value_length`),
  // AND the value itself (truncated to MAX_VALUE_LENGTH in domTracker.ts)
  // — VALUES ARE LOGGED BY DEFAULT. Add `data-log-value="false"` to a
  // specific field to opt it OUT (e.g. a free-text notes field you don't
  // want captured). type="password" is a hard block regardless of that
  // attribute — it can never be opted back in — see isSensitiveField() in
  // domTracker.ts.
  inputChanges: true,

  // `change` events on native <select> and MUI Select (MUI renders a real
  // hidden <input> for form compatibility, so this reuses the same
  // listener as inputChanges/checkboxRadioChanges under the hood). The
  // selected value IS LOGGED BY DEFAULT — dropdown options are a fixed
  // enumerable set of choices, not free text (e.g. "user changed status
  // filter to Accepted"). Add `data-log-value="false"` to a specific
  // select to opt it out if one of your dropdowns ever does carry
  // sensitive options.
  selectChanges: true,

  // `change` events on checkboxes/radio buttons. Logs the field identity +
  // the resulting checked boolean — always safe, no opt-out needed since
  // there's no free-text value involved.
  checkboxRadioChanges: true,

  // `submit` events on <form> elements. Logs the form's id/name/action
  // only — never field contents (those are covered/redacted individually
  // by inputChanges/selectChanges/checkboxRadioChanges above if enabled).
  formSubmit: true,

  // MUI Dialog/Modal/Drawer/Popover open + close, detected via a
  // MutationObserver watching for elements matching MODAL_SELECTOR (see
  // domTracker.ts) being added/removed from the DOM. This catches a modal
  // closing no matter how it happened — close button, Escape key, backdrop
  // click, or a programmatic close — without needing to instrument every
  // individual dialog component. Logs which dialog (from its title/
  // aria-label/data-log-id) and how long it stayed open.
  modalOpenClose: true,

  // `change` events on <input type="file">. Handled separately from
  // inputChanges above because browsers mask a file input's `.value` to a
  // fake path (`C:\fakepath\...`) for security — logging that would be
  // useless. Logs file name/size/type per selected file (capped at 10
  // files per event to avoid a bulk-upload payload getting huge) instead.
  fileInputChanges: true,

  // `error` events on <img> elements (registered with {capture:true} since
  // resource-load "error" events don't bubble — this is the standard way
  // to catch them via delegation). Logs the broken image's src/alt. Real
  // operational signal here specifically: this app is built around
  // displaying captured inspection images, so a 404/CORS-blocked defect
  // photo failing silently is worth catching like an API failure.
  imageLoadErrors: true,

  // document.visibilitychange, logged as its own TAB_HIDDEN/TAB_VISIBLE
  // event pair with how long the tab was in the previous state
  // (active_duration_ms / hidden_duration_ms). Distinct from the
  // visibilitychange listener in logger.ts that just flushes the buffer —
  // this one is purely for "was this page actually being watched" signal.
  tabVisibility: true,

  // Scroll depth milestones (25/50/75/100%) per page, NOT raw scroll
  // events — those fire dozens of times per second and would be pure
  // noise. Milestones reset whenever the pathname changes. Throttled via
  // requestAnimationFrame so it costs effectively nothing on scroll.
  scrollDepth: true,

  // `paste` events on inputs/textareas/contenteditable. Logs that a paste
  // happened + how many characters were pasted (`pasted_length`) — NEVER
  // the pasted content itself, regardless of any data-log-value attribute
  // on the target field. Useful for spotting bulk data entry vs manual
  // typing without capturing what was actually pasted.
  pasteDetection: true,

  // Web Vitals (CLS/FID/FCP/LCP/TTFB) via the web-vitals package, piped
  // through src/reportWebVitals.ts (already called in index.tsx, but
  // previously wired to a no-op — see the change there). Real page-load
  // performance data per user/page, close to free since the library was
  // already a dependency and already being invoked.
  webVitals: true,

  // NOT part of domTracker.ts / initDomTracking() — these two are checked
  // directly at their own instrumentation sites (they aren't generic DOM
  // events, so there's nothing to delegate at the document level for
  // them), but live in this same switchboard so there's still one place
  // to control everything.

  // WebSocket connect/disconnect/error (src/hooks/useAgentWebSocket.ts) and
  // SSE connect/close/error (src/modules/training/components/Training.tsx,
  // src/modules/training-new/components/TrainingMain.tsx). Neither was
  // covered before — only HTTP/axios calls were logged.
  webSocketEvents: true,

  // Drag-and-drop reorder actions via @dnd-kit — currently the only real
  // usage in the app is the analytics dashboard's custom graph layout
  // (src/modules/reports-analytics/.../ReportsAnalyticsCustomViewer.tsx).
  // recipe-builder and operator-panel-builder do NOT use dnd-kit despite
  // the "drag and drop" wording in their UI, so there was nothing to wire
  // up there.
  dndEvents: true,
};
